import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

const UserSidebar = () => {
  const [userInfo, setUserInfo] = useState({
    name: "",
    surname: "",
    company: "",
    admin: false,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [logoutHover, setLogoutHover] = useState(false);
  const [logoutActive, setLogoutActive] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const pathToLinkName = {
    "/user-dashboard": "dashboard",
    "/user-profile": "profilo",
    "/user-domains": "domini",
    "/user-orders": "pagamenti",
    "/help": "help",
  };

  const [activeLink, setActiveLink] = useState(() => {
    return pathToLinkName[location.pathname] || "dashboard";
  });

  useEffect(() => {
    const current = pathToLinkName[location.pathname] || "dashboard";
    setActiveLink(current);
  }, [location.pathname]);

  useEffect(() => {
    async function fetchUserInfo() {
      setLoading(true);
      setError("");
      try {
        const host = import.meta.env.VITE_HOST;
        const port = import.meta.env.VITE_PORT;
        const token = localStorage.getItem("jwt_token");
        const userId = localStorage.getItem("user_id");

        if (!token || !userId) {
          navigate("/login");
          return;
        }

        const url = `${host}:${port}/api/v1/users/user_info?_id=${userId}`;
        const res = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.status === 401) {
          localStorage.removeItem("jwt_token");
          localStorage.removeItem("user_id");
          navigate("/login");
          return;
        }

        if (!res.ok) {
          let errorMsg = `Errore HTTP ${res.status}`;
          try {
            const errData = await res.json();
            if (errData.detail) errorMsg = errData.detail;
          } catch {}
          throw new Error(errorMsg);
        }

        const response = await res.json();
        const data = response?.data?.[0];
        if (!data || !data.name || !data.surname) {
          throw new Error("Dati utente incompleti o non trovati.");
        }

        setUserInfo({
          name: data.name,
          surname: data.surname,
          company: data.company || "",
          admin: data.is_admin,
        });
      } catch (err) {
        setError(err.message || "Impossibile caricare i dati utente.");
      } finally {
        setLoading(false);
      }
    }

    fetchUserInfo();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("jwt_token");
    localStorage.removeItem("user_id");
    window.location.href = "/";
  };

  const menuItems = [
    { name: "dashboard", label: "Dashboard", to: "/user-dashboard" },
    { name: "profilo", label: "Profilo", to: "/user-profile" },
    { name: "domini", label: "Domini", to: "/user-domains" },
    { name: "pagamenti", label: "Pagamenti", to: "/user-orders" },
    { name: "help", label: "Help", to: "/help" },
  ];

  return (
    <div className="flex flex-col justify-between h-screen bg-white shadow-md w-64 py-10">
      {/* Sezione Top - Profilo */}
      <div className="text-center mb-8">
        <img
          src="https://cdn-icons-png.flaticon.com/512/1077/1077114.png"
          alt="Profilo Utente"
          className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
        />
        {loading ? (
          <p className="text-gray-500">Caricamento dati...</p>
        ) : (
          <>
            <h2 className="text-xl font-bold text-gray-900">
              {userInfo.name || "Nome"} {userInfo.surname || "Cognome"}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {userInfo.company || "Nessuna azienda"}
            </p>
          </>
        )}
        {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
      </div>

      {/* Sezione Centro - Menu */}
      <nav className="flex flex-col space-y-4 text-gray-700">
        {menuItems.map(({ name, label, to }) => {
          const isActive = activeLink === name;
          const [hover, setHover] = React.useState(false);

          return (
            <Link
              key={name}
              to={to}
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
              className={`
                relative ml-auto w-[80%] cursor-pointer rounded-l-lg
                py-2 transition-colors duration-300 ease-in-out
                ${
                  isActive
                    ? "bg-[#94c41c] text-white font-semibold"
                    : "bg-white text-black hover:bg-[#94c41c] hover:text-white"
                }
              `}
              style={{ overflow: "hidden" }}
              onClick={() => setActiveLink(name)}
            >
              <span
                style={{
                  display: "block",
                  paddingRight: "1rem",
                  transition: "transform 0.3s ease-in-out",
                  transform: isActive || hover ? "translateX(-10px)" : "translateX(0)",
                }}
              >
                {label}
              </span>
            </Link>
          );
        })}
      </nav>

      {/* Sezione Admin Link (solo per admin) */}
      {userInfo.admin && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={() => navigate("/admin-dashboard")}
            className="transition duration-200 text-black"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "1rem",
              fontWeight: "500",
              marginLeft: "50px",
              padding: "0.5rem 1rem",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "#94c41c";
              e.currentTarget.style.transform = "translateX(5px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "black";
              e.currentTarget.style.transform = "translateX(0)";
            }}
          >
            Area Admin
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 ml-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* Sezione Bottom - Pulsante Logout */}
      <div className="mt-auto flex justify-center mb-4">
        <button
          onClick={handleLogout}
          onMouseEnter={() => setLogoutHover(true)}
          onMouseLeave={() => setLogoutHover(false)}
          onMouseDown={() => setLogoutActive(true)}
          onMouseUp={() => setLogoutActive(false)}
          className={`
            relative ml-auto w-[80%] cursor-pointer rounded-l-lg py-2 
            bg-red-600 text-white font-semibold
            transition-colors duration-300 ease-in-out
            ${logoutHover ? "bg-red-700" : ""}
            ${logoutActive ? "bg-red-800" : ""}
          `}
          style={{
            overflow: "hidden",
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          }}
        >
          <span
            style={{
              display: "block",
              paddingRight: "1rem",
              transition: "transform 0.2s ease-in-out",
              transform: logoutActive
                ? "translateX(5px)"
                : logoutHover
                ? "translateX(5px)"
                : "translateX(0)",
            }}
          >
            Log Out
          </span>
        </button>
      </div>
    </div>
  );
};

export default UserSidebar;
